import styled, { css } from 'styled-components';

import { palettes, tokens } from '../../theme/tokens';

export type FieldStyleProps = {
	icon?: boolean;
	search?: boolean;
	disabled?: boolean;
	/** @deprecated The New UI field is a single size. */
	small?: boolean;
	valid?: boolean;
	error?: string | boolean;
	mergeWith?: 'top' | 'right' | 'bottom' | 'left';
	alternativeStyle?: 'enabled' | 'focused';
	$filled?: boolean;
	$multiline?: boolean;
	$trailing?: boolean;
};

export const INPUT_ICON_GAP = 7;

export const fieldStyle = css<FieldStyleProps>`
	border: 1px solid ${tokens.border.input};
	box-sizing: border-box;
	height: ${(props) => (props.$multiline ? 'auto' : '33px')};
	min-height: ${(props) => (props.$multiline ? '101px' : undefined)};
	padding: ${tokens.padding.compact}px ${tokens.padding.control}px;
	font-size: ${tokens.typography.sizes.label}px;
	font-weight: 500;
	line-height: ${(props) => (props.$multiline ? 1.4 : 1)};
	font-family: ${tokens.typography.fontFamily};
	width: 100%;
	-webkit-appearance: none;
	appearance: none;
	background: ${tokens.surface.white};
	color: ${tokens.text.primary};
	border-radius: ${tokens.radius.sm}px;
	box-shadow: ${tokens.shadow.input};
	transition:
		border-color 0.1s ease-out,
		background 0.1s ease-out;

	&::placeholder {
		color: ${tokens.text.tertiary};
		font-weight: 500;
	}

	${(props) =>
		!props.disabled &&
		css`
			&:hover,
			&[data-preview='hover'] {
				border-color: ${tokens.border.strong};
			}
		`}

	${(props) =>
		!props.valid &&
		!props.error &&
		css`
			&[placeholder]:not(:placeholder-shown):not(:focus):not(:hover):not(:disabled):not(
					[data-preview]
				) {
				border-color: ${tokens.border.subtle};
			}
		`}

	${(props) =>
		props.$filled &&
		!props.valid &&
		!props.error &&
		css`
			&:not(:focus):not(:hover):not(:disabled):not([data-preview]) {
				border-color: ${tokens.border.subtle};
			}
		`}

	&:focus,
	&[data-preview='focus'] {
		outline: none;
		border-color: ${tokens.border.focus};
	}

	${(props) =>
		props.icon &&
		`
		padding-left: ${tokens.padding.control + 16 + INPUT_ICON_GAP}px;
	`}

	${(props) =>
		props.$trailing &&
		`
		padding-right: ${tokens.padding.control + 16 + INPUT_ICON_GAP}px;
	`}

	${(props) =>
		props.valid &&
		css`
			border-color: ${tokens.success.border};

			&:hover:not(:disabled),
			&:focus,
			&[data-preview],
			&[placeholder]:not(:placeholder-shown):not(:focus):not(:hover):not(:disabled) {
				border-color: ${tokens.success.border};
			}
		`}

	${(props) =>
		props.error &&
		css`
			border-color: ${tokens.error.border};

			&:hover:not(:disabled),
			&:focus,
			&[data-preview],
			&[placeholder]:not(:placeholder-shown):not(:focus):not(:hover):not(:disabled) {
				border-color: ${tokens.error.border};
			}
		`}

	${(props) =>
		props.mergeWith === 'right' &&
		css`
			border-radius: ${tokens.radius.sm}px 0 0 ${tokens.radius.sm}px;
			:focus {
				position: relative;
				z-index: 1;
			}
		`}
	${(props) =>
		props.mergeWith === 'left' &&
		css`
			border-radius: 0 ${tokens.radius.sm}px ${tokens.radius.sm}px 0;
		`}
	${(props) =>
		props.disabled &&
		props.alternativeStyle === undefined &&
		css`
			background: ${palettes.heatherGray['100']};
			border-color: ${tokens.border.subtle};
			color: ${tokens.text.tertiary};

			&:hover,
			&[data-preview='hover'] {
				border-color: ${tokens.border.subtle};
			}

			&::placeholder {
				color: ${tokens.text.tertiary};
			}
		`}
	${(props) =>
		props.alternativeStyle === 'focused' &&
		css`
			outline: none;
			background: ${tokens.surface.white};
			border-color: ${tokens.border.focus};
			color: ${tokens.text.primary};
			box-shadow: ${tokens.shadow.input};
		`}
	${(props) =>
		props.alternativeStyle === 'enabled' &&
		css`
			background: ${tokens.surface.white};
			border-color: ${tokens.border.input};
			color: ${tokens.text.primary};
		`}

	&::-webkit-calendar-picker-indicator {
		opacity: 0.5;
	}

	&::-webkit-datetime-edit-hour-field,
	&::-webkit-datetime-edit-minute-field,
	&::-webkit-datetime-edit-second-field,
	&::-webkit-datetime-edit-ampm-field,
	&::-webkit-datetime-edit-meridiem-field {
		border-radius: ${tokens.radius.xs}px;
		padding-inline: 2px;
		background-color: ${palettes.softBeige['500']};
		color: ${tokens.text.primary} !important;
		transition: background-color 0.1s ease-out;

		&:hover:not(:focus):not(:disabled) {
			background-color: ${tokens.surface.secondary};
			cursor: pointer;
		}

		&:not(:focus) {
			background-color: transparent;
		}
	}

	&::-webkit-inner-spin-button {
		opacity: 1;
		transition: opacity 0.1s ease-out;
		transition-delay: 0.1s;
	}

	&:not(:focus, :hover)::-webkit-inner-spin-button {
		opacity: 0;
		transition-delay: 0s;
	}
`;

export const InputLabel = styled.label`
	display: block;
	width: 100%;
	color: ${tokens.text.primary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.label}px;
	font-weight: ${tokens.typography.weights.medium};
	letter-spacing: 0.14px;
	line-height: ${tokens.typography.lineHeights.heading};
`;

export const InputDescription = styled.p`
	width: 100%;
	margin: 0;
	color: ${tokens.text.secondary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.small}px;
	font-weight: 450;
	letter-spacing: 0.13px;
	line-height: ${tokens.typography.lineHeights.small};
`;

export const InputError = styled.p`
	width: 100%;
	margin: 0;
	color: ${tokens.error.text};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.small}px;
	line-height: ${tokens.typography.lineHeights.small};
`;

export const InputGroup = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: ${tokens.gap.element}px;
	width: 100%;
`;
